export interface StepperStep {
  id: string;
  showStepper?: boolean | null;
}

export interface StepperState {
  /** Whether the stepper UI should be rendered for the active step. */
  showStepper: boolean;
  /**
   * 1-based position within the visible steps only.
   * 0 when the active step is itself hidden (showStepper === false).
   */
  currentStep: number;
  /** Count of steps where showStepper is not false. */
  totalSteps: number;
  /**
   * True when the active step is the first step in the visible list.
   * Used to suppress the back button on the first stepper-visible step.
   */
  isFirstStepperStep: boolean;
}

/**
 * Derives stepper display state from a step list and the currently active step id.
 *
 * Interpretation A: steps with showStepper === false are excluded entirely from
 * both currentStep and totalSteps. A hidden intro step means the next visible
 * step shows "Step 1 of N-1", not "Step 2 of N".
 *
 * This is a plain function (not a React hook) so it can be called from hooks,
 * components, and tests without a render context.
 */
export function getStepperState(
  steps: StepperStep[],
  activeStepId: string,
): StepperState {
  const visibleSteps = steps.filter((s) => s.showStepper !== false);
  const activeStep = steps.find((s) => s.id === activeStepId);
  const showStepper = activeStep?.showStepper !== false;

  const visibleIndex = visibleSteps.findIndex((s) => s.id === activeStepId);

  return {
    currentStep: visibleIndex === -1 ? 0 : visibleIndex + 1,
    isFirstStepperStep: visibleIndex === 0,
    showStepper,
    totalSteps: visibleSteps.length,
  };
}
